import { initializeSingletonMVUReducer } from '~/mvuReducer'
import {
  applyAction,
  FOUR_HOURS_IN_SECONDS,
  initialize,
  makeSideEffectExecutor,
  StartRecordingPolicy,
  UploadAttrs,
  WebRecorder2Action,
  WebRecorder2SideEffect,
  WebRecorder2State,
} from './webRecorder2'
import {
  broadcastRecordingState,
  clearCriticalBanner,
  serverLog,
  setCriticalBanner,
  setSubjectId,
  trackAnalytics,
  uploadAndVerifyRecording,
} from './liveView'
import { Hook } from '~/hooks/Hook'
import { AudioState } from './recorder'
import { extractSubjectId } from '~/utils/string'
import { mostRecentUnsubmittedRecording } from './recordingStorage'

let hook: Hook | undefined = undefined

export function setHook(newHook: Hook) {
  hook = newHook
}

type LogLevel = 'debug' | 'info' | 'warn' | 'error'

export function setUpWebRecorder() {
  if (window.location.pathname.includes('/admin/')) {
    return
  }

  const rootElement = document.querySelector('[phx-hook="WebRecorder2Hook"]')! as HTMLElement

  if (!rootElement) {
    /* eslint-disable-next-line no-console */
    console.error('No WebRecorder2Hook found at page load; web recording will fail.')
    return
  }

  const callbacks = {
    upload: (attrs: UploadAttrs) => hook && uploadAndVerifyRecording(hook, attrs),
    /* eslint-disable-next-line no-console */
    log: (message: string) => console.log(message),
    trackAnalytics: (msg: string, extra?: Record<string, any>) => hook && trackAnalytics(hook, msg, extra),
    serverLog: (level: LogLevel, message: string, metadata?: Record<string, any>) =>
      hook && serverLog(hook, level, message, metadata),
    setCriticalBanner: (msg: string) => hook && setCriticalBanner(hook, msg),
    clearCriticalBanner: () => hook && clearCriticalBanner(hook),
    broadcastRecordingState: (state: AudioState) => hook && broadcastRecordingState(hook, state),
    setSubjectId: (subjectId: string) => hook && setSubjectId(hook, subjectId),
  }

  const canUseWebRecorder = rootElement.hasAttribute('data-can-use-web-recorder')
  const clientId = rootElement.dataset.clientId!
  const recordingDisclosureAudioPath = rootElement.dataset.recordingDisclosureAudioPath ?? ''

  const { subjectId: defaultSubjectId, startRecording } = canUseWebRecorder
    ? defaultSubject()
    : { subjectId: undefined, startRecording: 'no' as const }

  const maxRecordingDurationSeconds = parseInt(
    rootElement.dataset.maxRecordingDurationSeconds || String(FOUR_HOURS_IN_SECONDS),
    10
  )

  const loggedApplyAction = canUseWebRecorder
    ? (state: WebRecorder2State, action: WebRecorder2Action) => {
        const result = applyAction(state, action)

        if (result && state) {
          const [newState, sideEffects] = result

          // Only track if there are meaningful changes (similar to debugLogUpdate logic)
          const hasStateChange = !isStateRoughlyEqual(state, newState)
          const hasMeaningfulSideEffects = sideEffects.some(shouldTrackSideEffect)

          if (hasStateChange || hasMeaningfulSideEffects) {
            callbacks.serverLog('info', 'web-recorder:action', {
              action: action.action,
              from_state: state.mode,
              to_state: newState.mode,
              has_recorder: 'recorder' in state,
              has_storage: 'storage' in state,
              is_online: state.isOnline,
              side_effects: sideEffects.map((effect) => effect.effect),
              timestamp: Date.now(),
            })
          }
        }

        return result
      }
    : (state: WebRecorder2State, action: WebRecorder2Action) => {
        /* eslint-disable-next-line no-console */
        console.log('[WebRecorder2] Refusing to apply action since web recorder is disabled', action)
        return [state, []] as [WebRecorder2State, WebRecorder2SideEffect[]]
      }

  initializeSingletonMVUReducer(
    'webRecorder2',
    initialize({
      canUseWebRecorder,
      subjectId: defaultSubjectId,
      recordingDisclosureAudioPath,
      clientId,
      startRecording,
      maxDurationSeconds: maxRecordingDurationSeconds,
    }),
    loggedApplyAction,
    makeSideEffectExecutor(callbacks)
  )

  // If you navigate away from Jump, let the backend know this tab is no longer recording
  window.addEventListener('beforeunload', () => hook && broadcastRecordingState(hook, 'stopped'))
}

// If you're on a /meetings/mtg_* or /prepare/int_ref_* page, we'll default to the subject ID from the URL.
// Otherwise, we should see if you were recently recording another meeting, and if so, continue that.
function defaultSubject(): {
  subjectId?: string
  startRecording: StartRecordingPolicy
} {
  const subjectIdFromUrl = window.location.pathname.match(/^\/(meetings|prepare)\/([^/?]+)/)?.at(2)
  const extractedSubjectId = subjectIdFromUrl ? extractSubjectId(subjectIdFromUrl) : null
  const isRecordable = meetingIsRecordable()

  if (subjectIdFromUrl && extractedSubjectId && isRecordable) {
    const startRecording = window.location.search.includes('capture_now=true') ? 'ifNoPreviousRecordingExists' : 'no'
    return {
      subjectId: subjectIdFromUrl,
      startRecording,
    }
  } else {
    const last24HoursInMs = 24 * 60 * 60 * 1_000
    const recordingToContinue = mostRecentUnsubmittedRecording({
      withinLastMs: last24HoursInMs,
    })

    const subjectId = recordingToContinue
      ? 'meetingId' in recordingToContinue
        ? recordingToContinue.meetingId
        : recordingToContinue.intRefId
      : undefined

    const startRecording = recordingToContinue?.recordingInProgress ? 'immediately' : 'no'

    return { subjectId, startRecording }
  }
}

function meetingIsRecordable() {
  const meetingStateElement = document.getElementById('react-meeting-state')
  const hasRecordableAttribute = meetingStateElement?.hasAttribute('data-is-recordable')

  return hasRecordableAttribute
}

// Helper function to determine if state actually changed meaningfully
function isStateRoughlyEqual(state1: WebRecorder2State, state2: WebRecorder2State): boolean {
  // Compare key state properties that indicate meaningful changes
  return (
    state1.mode === state2.mode &&
    'recorder' in state1 === 'recorder' in state2 &&
    'storage' in state1 === 'storage' in state2 &&
    state1.isOnline === state2.isOnline &&
    state1.errorMessage === state2.errorMessage
  )
}

// Helper function to determine if side effect should be tracked
function shouldTrackSideEffect(sideEffect: WebRecorder2SideEffect): boolean {
  // Only track meaningful side effects, skip routine ones
  const routineEffects = ['log', 'clear-critical-alert']
  return !routineEffects.includes(sideEffect.effect)
}
